/**
 * PATH: packages/email-hub/src/feature/components/email-allocation/useEmailAllocationRail.ts
 * FILE_ID: EMAILHUB-ALLOCATION-HOOK-001
 * PURPOSE: Composes assignment, due-bucket, and watcher hooks into one rail VM.
 */
import { useState } from 'react'

import { useToast } from '../../../_internal/components/Toast'
import { useEmailActions } from '../../hooks/useEmails'
import type {
  EmailAllocationRailProps,
  EmailAllocationRailViewModel,
} from './types'
import { useAssignment } from './useAssignment'
import { useDueBucket } from './useDueBucket'
import { useWatchers } from './useWatchers'

export function useEmailAllocationRail({
  emailId,
  assignment,
  watchers,
  members,
  onAssignmentChanged,
  onWatchersChanged,
}: EmailAllocationRailProps): EmailAllocationRailViewModel {
  const { toast } = useToast()
  const {
    setEmailAssignment,
    clearEmailAssignment,
    inviteEmailWatcher,
    removeEmailWatcher,
  } = useEmailActions()
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [showAssigneePicker, setShowAssigneePicker] = useState(false)
  const [showWatcherPicker, setShowWatcherPicker] = useState(false)

  const assignmentViewModel = useAssignment({
    emailId,
    assignment,
    members,
    busyKey,
    setBusyKey,
    showPicker: showAssigneePicker,
    setShowPicker: setShowAssigneePicker,
    setEmailAssignment,
    clearEmailAssignment,
    onAssignmentChanged,
    toast,
  })
  const dueBucketViewModel = useDueBucket({
    emailId,
    assignment,
    busyKey,
    setBusyKey,
    setEmailAssignment,
    onAssignmentChanged,
    toast,
  })
  const watchersViewModel = useWatchers({
    emailId,
    watchers,
    members,
    assigneeUserId: assignmentViewModel.assigneeUserId,
    busyKey,
    setBusyKey,
    showPicker: showWatcherPicker,
    setShowPicker: setShowWatcherPicker,
    inviteEmailWatcher,
    removeEmailWatcher,
    onWatchersChanged,
    toast,
  })

  return {
    assignment: assignmentViewModel,
    dueBucket: dueBucketViewModel,
    watchers: watchersViewModel,
  }
}
